import differenceWith from 'lodash/differenceWith';
import find from 'lodash/find';
import filter from 'lodash/filter';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import reduce from 'lodash/reduce';

import { ApiService } from 'services/api.service';
import { StoreInfoService } from 'services/storeInfo.service';
import 'constants/constants.json';
import {
    createCartAddress,
    isGiftCardInLegacyCart,
    isGiftCardInCart,
    getSavedCartInfo,
    setSavedCartInfo
} from './cartUtils';
import { ShipToLocationsService } from './shipToLocations.service';

export const CheckoutService = angular
    .module('checkout.checkoutService', [
        ApiService,
        StoreInfoService,
        ShipToLocationsService,
        'app.constants',
        'ngMaterial'
    ]).service('checkoutService', (
        $q,
        apiService,
        storeInfoService,
        shipToLocationsService,
        $mdDialog,
        PAYMENT_TYPES,
        $window
    ) => {
        let cartId;

        function setCartId(id) {
            cartId = id;
        }

        function isGiftCodeAlreadyApplied(code, storeCreditInfo) {
            const appliedCodes = (storeCreditInfo.items || [])
                .reduce((acc, item) => [...acc, item.giftCardCode], []);
            return appliedCodes.includes(code.toUpperCase());
        }

        function applyPromoCode(code, storeCreditInfo = {}, currentCart, currentCartGrandTotal) {
            if (isGiftCodeAlreadyApplied(code, storeCreditInfo)) {
                return $q.reject(new Error('giftCardAlreadyApplied'));
            }

            return apiService.getStoreCreditByCode(code)
                .then((storeCredit) => {
                    if (storeCredit.status !== 'active') {
                        throw new Error('inactiveGiftCard');
                    }
                    if (isGiftCardInCart(currentCart.items)) {
                        throw new Error('giftCardNotAllowed');
                    }
                    const duplicateIndex = storeCreditInfo.items.findIndex(item => item.giftCardCode === code);
                    if (duplicateIndex > -1) {
                        storeCreditInfo.items[duplicateIndex] = storeCredit;
                    } else {
                        if (storeCreditInfo.storeCreditAmountInCents / 100 >= currentCartGrandTotal) {
                            throw new Error('cannotAddMoreStoreCredit');
                        }
                        storeCreditInfo.items.push(storeCredit);
                    }

                    return currentCart;
                }).catch((error) => {
                    if (error.message === 'inactiveGiftCard') {
                        throw new Error('inactiveGiftCard');
                    }
                    if (error.message === 'giftCardNotAllowed') {
                        throw new Error('giftCardNotAllowed');
                    }
                    if (error.message === 'cannotAddMoreStoreCredit') {
                        throw new Error('cannotAddMoreStoreCredit');
                    }
                    return apiService.addDiscountCodeToCart(code, currentCart.cartId)
                        .then(() => apiService.getCart(currentCart.cartId))
                        .catch((e) => {
                            if (e.status === 404) {
                                throw new Error('promoCodeNotFound');
                            }
                            if (e.status === 409) {
                                throw new Error('discountCodeExistsInCart');
                            }
                            // TODO: add checks for other 4XX code errors, e.g.
                            //       discount code found but expired,
                            //       discount code found but has no usages left

                            // the "catch-all" error to ensure an error is bubbled up
                            throw new Error('Unhandled error');
                        });
                });
        }

        function legacyApplyPromoCode(code, storeCreditInfo = {}, currentCart) {
            if (isGiftCodeAlreadyApplied(code, storeCreditInfo)) {
                return $q.reject(new Error('giftCardAlreadyApplied'));
            }

            return apiService.getStoreCreditByCode(code).then((storeCredit) => {
                if (storeCredit.status !== 'active') {
                    throw new Error('inactiveGiftCard');
                }
                if (isGiftCardInLegacyCart(currentCart.items)) {
                    throw new Error('giftCardNotAllowed');
                }
                const duplicateIndex = storeCreditInfo.items.findIndex(item => item.giftCardCode === code);
                if (duplicateIndex > -1) {
                    storeCreditInfo.items[duplicateIndex] = storeCredit;
                } else {
                    if (storeCreditInfo.storeCreditAmountInCents / 100 >= currentCart.grandTotal) {
                        throw new Error('cannotAddMoreStoreCredit');
                    }
                    storeCreditInfo.items.push(storeCredit);
                }
                return getAndUpdateCart((cart) => {
                    return {
                        ...cart
                    };
                });
            }).catch((error) => {
                if (error.message === 'inactiveGiftCard') {
                    throw new Error('inactiveGiftCard');
                }
                if (error.message === 'giftCardNotAllowed') {
                    throw new Error('giftCardNotAllowed');
                }
                if (error.message === 'cannotAddMoreStoreCredit') {
                    throw new Error('cannotAddMoreStoreCredit');
                }
                return getAndUpdateCart((cart) => {
                    return {
                        ...cart,
                        discountCode: code
                    };
                });
            });
        }

        function createCartAddressFromForm(addressFormModel) {
            return createCartAddress(
                addressFormModel.address1,
                addressFormModel.address2,
                addressFormModel.city,
                addressFormModel.state,
                addressFormModel.postalCode,
                addressFormModel.country,
                addressFormModel.firstName,
                addressFormModel.lastName,
                addressFormModel.phone
            );
        }

        function removeDiscountCodeItem(discountCode) {
            return apiService.removeDiscountCodeFromCart(discountCode, cartId)
                .then(() => apiService.getCart(cartId));
        }

        /**
         * accepts ID as either a string or an array of strings
         * @param discountId
         */
        function legacyRemoveDiscountCodeItem(discountId) {
            return getAndUpdateCart((cart) => {
                return _returnCartWithUpdatedDiscounts(cart, discountId);
            });
        }

        function _returnCartWithUpdatedDiscounts(cart, discountId) {
            return {
                ...cart,
                discountCode: null,
                discount: null,
                discounts: _removeDiscountFromArray(cart.discounts, discountId)
            };
        }

        function _removeDiscountFromArray(discounts, discountId) {
            if (isArray(discountId)) {
                const dif = differenceWith(discounts, discountId, (item, id) => {
                    return item.discount.id === id;
                });
                return dif;
            }
            return discounts.filter(appliedDiscount => appliedDiscount.discount.id !== discountId);
        }

        function applyContactInfoAndShippingAddressToCart(stepOneData) {
            const validateCountry = stepOneData.shippingAddressModel.country ?
                shipToLocationsService.isCountryAvailable(stepOneData.shippingAddressModel.country) :
                $q.resolve();

            return validateCountry.then(() => {
                return service.getAndUpdateCart((cart) => {
                    const cartAddress = createCartAddressFromForm(stepOneData.shippingAddressModel);
                    cartAddress.phoneNumber = stepOneData.contactInfoModel.phone;

                    return {
                        ...cart,
                        shippingAddress: cartAddress,
                        billingAddress: stepOneData.shippingAddressModel.shippingAsBilling ? cartAddress : {},
                        selectedShippingAddressId: undefined,
                        contactInfo: {
                            email: stepOneData.contactInfoModel.email,
                            phoneNumber: stepOneData.contactInfoModel.phone
                        },
                        emailOptIn: stepOneData.contactInfoModel.emailOptIn,
                        postalCode: stepOneData.shippingAddressModel.postalCode,
                        state: stepOneData.shippingAddressModel.state,
                        country: stepOneData.shippingAddressModel.country
                    };
                });
            });
        }

        function saveContactInfoAndShippingAddress(stepOneData) {
            const validateCountry = stepOneData.shippingAddressModel.country ?
                shipToLocationsService.isCountryAvailable(stepOneData.shippingAddressModel.country) :
                $q.resolve();

            return validateCountry.then(() => {
                const cartAddress = createCartAddressFromForm(stepOneData.shippingAddressModel);
                cartAddress.phoneNumber = stepOneData.contactInfoModel.phone;
                setSavedCartInfo({
                    ...getSavedCartInfo(),
                    billingAddress: stepOneData.shippingAddressModel
                        .shippingAsBilling
                        ? cartAddress
                        : {},
                    selectedShippingAddressId: undefined,
                    contactInfo: {
                        email: stepOneData.contactInfoModel.email,
                        phoneNumber: stepOneData.contactInfoModel.phone
                    },
                    emailOptIn: stepOneData.contactInfoModel.emailOptIn,
                    postalCode: stepOneData.shippingAddressModel.postalCode,
                    state: stepOneData.shippingAddressModel.state,
                    country: stepOneData.shippingAddressModel.country,
                    shippingAddress: cartAddress
                });

                return $q.resolve();
            });
        }

        function applyUpdatedPhoneNumberToShopper(shopperAccount, phoneNumber) {
            shopperAccount.contactInformation.phoneNumber = phoneNumber;
            return apiService.account.update(shopperAccount);
        }

        function applyShippingMethodToCartObject(cart, selectedShippingMethodIndex) {
            const cartObject = {
                ...cart,
                shippingMethod: {
                    name: cart.availableShippingMethods[selectedShippingMethodIndex].name,
                    shippingBand: {
                        shippingPrice: cart.availableShippingMethods[selectedShippingMethodIndex].shippingPrice
                    }
                }
            };

            if (isCurbsidePickupShippingMethod(cart.availableShippingMethods[selectedShippingMethodIndex])) {
                cartObject.shippingMethod.pickupAddress =
                    cart.availableShippingMethods[selectedShippingMethodIndex].pickupAddress;
            }

            return cartObject;
        }

        function isCurbsidePickupShippingMethod(shippingMethod) {
            return shippingMethod.name === 'Curbside Pickup';
        }

        function applyShippingMethodToCart(selectedShippingMethodIndex) {
            return service.getAndUpdateCart(
                cart => applyShippingMethodToCartObject(cart, selectedShippingMethodIndex)
            );
        }

        function applyBillingAddressToCart(billingAddressModel) {
            return service.getAndUpdateCart((cart) => {
                return {
                    ...cart,
                    billingAddress: createCartAddressFromForm(billingAddressModel)
                };
            });
        }

        function applyCheckoutUrl(returnUri, bagId) {
            return getAndUpdateCart((cart) => {
                return {
                    ...cart,
                    checkoutUrl: `${returnUri}/checkout/?bag=${bagId}`
                };
            });
        }

        function getAndUpdateCart(updateCartLogic) {
            return _getAndUpdateCart(updateCartLogic)
                .catch((error) => { // Retry in case of 409
                    if (isConflictError(error)) return _getAndUpdateCart(updateCartLogic);
                    throw error;
                });
        }

        function _getAndUpdateCart(updateCartLogic) {
            return apiService.getLegacyCart(cartId)
                .then(cart => updateCartLogic(cart))
                .then(updatedCart => apiService.updateLegacyCart(cartId, updatedCart));
        }

        function isConflictError(error) {
            return (error.data && error.data.statusCode === 409) || error.status === 409;
        }

        function showErrorDialog(message, title = 'Error', reload = false) {
            const buttonText = (reload) ? 'Return to Checkout' : 'OK';
            return $mdDialog.show(
                $mdDialog.alert({
                    clickOutsideToClose: true,
                    title,
                    textContent: message,
                    ariaLabel: 'Error',
                    escapeToClose: true,
                    ok: buttonText,
                    onComplete: () => {
                        checkModalTopPositioning();
                    }
                })
            )
                .then(() => {
                    if (reload) $window.location.reload();
                });
        }

        function checkModalTopPositioning() {
            const bodyTopPosition = parseInt(
                $window.getComputedStyle($window.document.body).top
            );

            if ($window.document.querySelector('.md-dialog-container')) {
                const dialogTopPosition = parseInt(
                    $window.getComputedStyle($window.document.querySelector('.md-dialog-container')).top
                );

                if ((bodyTopPosition + dialogTopPosition) !== 0) fixModalTopPositioning(dialogTopPosition);
            }
        }

        function fixModalTopPositioning(dialogTopPosition) {
            $window.document.querySelector('.md-dialog-container').style.top = `${(parseInt(dialogTopPosition) / 2)}px`;
        }

        function getAvailablePaymentMethods() {
            return storeInfoService.get().then((storeInfo) => {
                const availablePaymentTypes = [];

                const authorizeNet = storeInfo.authorizeNet;
                const acceptsAuthorizeNet = !!(authorizeNet && authorizeNet.isEnabled);
                const acceptsPaypal = (storeInfo.paypal && storeInfo.paypal.isEnabled);
                const acceptsPayPalAcp = (storeInfo.paypal &&
                    storeInfo.paypal.acpStatus === 'SUBSCRIBED' &&
                    !storeInfo.paypal.acpLimitReached
                );

                if (storeInfo.acceptsStripeAsPayment || acceptsAuthorizeNet || acceptsPayPalAcp) {
                    availablePaymentTypes.push(find(PAYMENT_TYPES, { value: 'Credit Card' }));
                }

                if (acceptsPaypal) {
                    availablePaymentTypes.push(find(PAYMENT_TYPES, { value: 'PayPal' }));
                }

                if (!acceptsPaypal && !storeInfo.acceptsStripeAsPayment && !acceptsAuthorizeNet) {
                    availablePaymentTypes.push(find(PAYMENT_TYPES, { value: 'Cash' }));
                }

                return availablePaymentTypes;
            });
        }

        function getOptimalDefaultPaymentMethod(paymentMethods) {
            const hasPayPal = find(paymentMethods, { value: 'PayPal' });
            const hasCard = find(paymentMethods, { value: 'Credit Card' });

            let defaultPaymentMethod;

            if (hasCard) {
                defaultPaymentMethod = 'Credit Card';
            } else if (hasPayPal) {
                defaultPaymentMethod = 'PayPal';
            } else {
                defaultPaymentMethod = 'Cash';
            }

            return defaultPaymentMethod;
        }

        function forceFormValidation(form) {
            if (form) {
                angular.forEach(form.$error.required, (field) => {
                    field.$setDirty();
                    field.$setTouched();
                });
            }
        }

        function validateDiscounts(cart) {
            const deferred = $q.defer();
            const discounts = cart.discounts;
            if (!discounts || discounts.length === 0) return ok();
            if (!cart.contactInfo || !cart.contactInfo.email) return error('Email Required');

            const validationPromises = discounts.map((discount) => {
                return apiService.getDiscountValidity(cart.contactInfo.email, discount.discount.id);
            });

            let errorMessage;
            return $q.all(validationPromises)
                .then((results) => {
                    const invalidDiscounts = filter(results, ['isValid', false]);

                    const baseMessage = (invalidDiscounts.length === 1)
                        ? 'The following discount has already been used:'
                        : 'The following discounts have already been used:';
                    errorMessage = reduce(invalidDiscounts, (result, discount) => {
                        return `${result} ${discount.code || discount.name}`;
                    }, baseMessage);

                    return invalidDiscounts;
                })
                .then((invalidDiscounts) => {
                    if (invalidDiscounts.length === 0) return ok();
                    const discountIds = map(invalidDiscounts, 'id');
                    return error(errorMessage, discountIds);
                });

            function ok() {
                deferred.resolve('OK');
                return deferred.promise;
            }
            function error(message, invalidDiscounts = null) {
                const err = {
                    title: (invalidDiscounts) ? 'Discount no longer valid' : 'Error',
                    message,
                    invalidDiscounts
                };
                deferred.reject(err);
                return deferred.promise;
            }
        }

        const service = {
            applyBillingAddressToCart,
            applyCheckoutUrl,
            applyContactInfoAndShippingAddressToCart,
            applyPromoCode,
            applyShippingMethodToCart,
            applyShippingMethodToCartObject,
            applyUpdatedPhoneNumberToShopper,
            createCartAddressFromForm,
            forceFormValidation,
            getAndUpdateCart,
            getAvailablePaymentMethods,
            getOptimalDefaultPaymentMethod,
            legacyApplyPromoCode,
            legacyRemoveDiscountCodeItem,
            removeDiscountCodeItem,
            saveContactInfoAndShippingAddress,
            setCartId,
            showErrorDialog,
            validateDiscounts
        };

        return service;
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/checkout.service.js